import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  VStack,
  Text,
  Box,
  HStack
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSound } from '../../contexts/SoundContext.tsx';
import { useGame } from '../../contexts/GameContext.tsx';

interface Power {
  id: string;
  name: string;
  description: string;
  image: string;
  value: number;
  chance: number;
  slotId?: number; // Pour l'affichage en roulette
}

interface PowerRouletteProps {
  isOpen: boolean;
  onClose: () => void;
  onPowerSelected: (power: Power) => void;
  maxStreakLoss: number;
  targetPlayerName: string;
  attackerPlayerName: string;
  isAttackerWinner: boolean;
  preSelectedPower?: any;
  // Multiplayer-specific config: when provided, overrides local context config
  rouletteConfig?: Record<string, { enabled: boolean; chance: number }>;
  // Seed to ensure identical roulette display across clients
  seedKey?: string;
}

const PowerRoulette: React.FC<PowerRouletteProps> = ({
  isOpen,
  onClose,
  onPowerSelected,
  maxStreakLoss,
  targetPlayerName,
  attackerPlayerName,
  isAttackerWinner,
  preSelectedPower,
  rouletteConfig,
  seedKey
}) => {
  const { t } = useTranslation();
  const { playSound } = useSound();
  const { state } = useGame();
  
  const [selectedPower, setSelectedPower] = useState<Power | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentHighlight, setCurrentHighlight] = useState(0);
  
  // NOUVELLE APPROCHE CRÉATIVE : Roulette avec cases fixes et highlight mobile
  const basePowers: Power[] = [
    // Pouvoirs malus (retirent des points) - valeurs positives
    { id: 'minus1', name: '-1 Point', description: 'Retire 1 point de streak', image: '/power/minus1.png', value: 1, chance: 35 },
    { id: 'minus2', name: '-2 Points', description: 'Retire 2 points de streak', image: '/power/minus2.png', value: 2, chance: 25 },
    { id: 'minus3', name: '-3 Points', description: 'Retire 3 points de streak', image: '/power/minus3.png', value: 3, chance: 15 },
    // Pouvoirs bonus (ajoutent des points aléatoires) - valeurs négatives pour distinction
    { id: 'plus1', name: '+1 Point', description: 'Ajoute 1 point de streak aléatoire', image: '/power/plus1.png', value: -1, chance: 30 },
    { id: 'plus2', name: '+2 Points', description: 'Ajoute 2 points de streak aléatoires', image: '/power/plus2.png', value: -2, chance: 20 },
    { id: 'plus3', name: '+3 Points', description: 'Ajoute 3 points de streak aléatoires', image: '/power/plus3.png', value: -3, chance: 10 },
    // Pouvoirs swap (volent des points) - valeurs avec préfixe 100 pour distinction
    { id: 'swap1', name: 'Vol 1 Point', description: 'Vole 1 point de streak à la cible', image: '/power/swap1.png', value: 101, chance: 35 },
    { id: 'swap2', name: 'Vol 2 Points', description: 'Vole 2 points de streak à la cible', image: '/power/swap2.png', value: 102, chance: 25 },
    { id: 'swap3', name: 'Vol 3 Points', description: 'Vole 3 points de streak à la cible', image: '/power/swap3.png', value: 103, chance: 15 }
  ,
  // Nouveaux pouvoirs spéciaux
  { id: 'blocked', name: 'Bloqué', description: 'Bloque la meilleure catégorie pendant 3 tours', image: '/power/bloked.png', value: 0, chance: 10 },
  { id: 'nuke', name: 'Nuke', description: 'Réinitialise les streaks de tous les joueurs', image: '/power/nuke.png', value: 0, chance: 5 },
  { id: 'blind', name: 'Aveugle', description: 'Masque les catégories pendant 3 tours', image: '/power/blind.png', value: 0, chance: 10 },
  { id: 'megaswap', name: 'Mega Swap', description: 'Mélange les avancements de tous les joueurs', image: '/power/megaswap.png', value: 0, chance: 5 },
  { id: 'translate', name: 'Traduction', description: 'Questions dans une langue aléatoire pendant 5 tours', image: '/power/translate.png', value: 0, chance: 8 },
  { id: 'hardcore', name: 'Hardcore', description: 'Questions au niveau max pendant 3 tours', image: '/power/hardcore.png', value: 0, chance: 8 }
  ];

  // Créer une roulette avec de vraies cases visibles
  const createVisibleRoulette = (): Power[] => {
    const rouletteSlots: Power[] = [];
    // Lire la config (multiplayer > local advanced)
    const config = rouletteConfig || (state.advanced?.roulettePowers as any) || {};

    // Filtrer les pouvoirs disponibles selon les règles et la config avancée
    const filtered = basePowers.filter(power => {
      // Vérifier activation via config (par défaut: activé)
      const cfg = (config as any)[power.id];
      if (cfg && cfg.enabled === false) return false;
      // Toujours autoriser les pouvoirs spéciaux
      const isSpecial = ['blocked','nuke','blind','megaswap','translate','hardcore'].includes(power.id);
      if (isSpecial) return true;
      if (power.value > 0 && power.value < 100) {
        // Pouvoirs malus : limités par maxStreakLoss fourni par le serveur
        return power.value <= Math.max(0, maxStreakLoss || 0);
      } else if (power.value < 0) {
        // Pouvoirs bonus : toujours disponibles
        return true;
      } else if (power.value >= 100) {
        // Pouvoirs swap : limités par maxStreakLoss
        const swapAmount = power.value - 100;
        return swapAmount <= Math.max(0, maxStreakLoss || 0);
      }
      return false;
    });
    
    // Appliquer les poids (weights) issus de la config; fallback sur chance par défaut
    const availablePowers = filtered.map(p => {
      const cfg = (config as any)[p.id] || {};
      const raw = typeof cfg.weight === 'number' ? cfg.weight : (typeof cfg.chance === 'number' ? cfg.chance : p.chance);
      const weight = Math.max(0, raw || 0);
      return { ...p, chance: weight } as Power;
    });
    
    // Créer 12 cases (pour une roulette circulaire esthétique) avec RNG déterministe
    const totalWeight = availablePowers.reduce((sum, p) => sum + (p.chance || 0), 0);
    if (availablePowers.length === 0 || totalWeight <= 0) {
      // Fallback minimal pour éviter les blocages: fournir quelques cases par défaut
      return [
        { id: 'blocked', name: 'Bloqué', description: 'Bloque 3 tours', image: '/power/bloked.png', value: 0, chance: 1, slotId: 0 },
        { id: 'blind', name: 'Aveugle', description: 'Masque 3 tours', image: '/power/blind.png', value: 0, chance: 1, slotId: 1 },
        { id: 'nuke', name: 'Nuke', description: 'Reset streaks', image: '/power/nuke.png', value: 0, chance: 1, slotId: 2 }
      ] as Power[];
    }
    // RNG déterministe basé sur seedKey
    const makeSeed = (s: string) => {
      let h = 2166136261 >>> 0;
      for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 16777619);
      }
      return h >>> 0;
    };
    const seed = makeSeed((seedKey || `${attackerPlayerName}|${targetPlayerName}|${maxStreakLoss}|${preSelectedPower?.id || ''}`) + '|v1');
    let stateR = seed;
    const rand = () => {
      // xorshift32
      stateR ^= stateR << 13; stateR >>>= 0;
      stateR ^= stateR >> 17; stateR >>>= 0;
      stateR ^= stateR << 5;  stateR >>>= 0;
      return (stateR >>> 0) / 4294967296;
    };
    for (let i = 0; i < 12; i++) {
      const rv = rand() * totalWeight;
      let cumulative = 0;
      let selectedPower = availablePowers[0];
      for (const power of availablePowers) {
        cumulative += (power.chance || 0);
        if (rv <= cumulative) { selectedPower = power; break; }
      }
      rouletteSlots.push({ ...selectedPower, slotId: i });
    }

    // S'assurer que le pouvoir présélectionné par le serveur est présent au moins une fois
    if (preSelectedPower) {
      const exists = rouletteSlots.some(s => s.id === preSelectedPower.id);
      if (!exists) {
        // Remplacer la première case par le pouvoir présélectionné pour garantie
        rouletteSlots[0] = { ...availablePowers.find(p => p.id === preSelectedPower.id) || basePowers.find(p => p.id === preSelectedPower.id) || rouletteSlots[0], slotId: 0 } as Power;
      }
    }
    
    return rouletteSlots;
  };

  const [rouletteSlots, setRouletteSlots] = useState<Power[]>([]);

  useEffect(() => {
  if (isOpen) {
      setRouletteSlots(createVisibleRoulette());
      setCurrentHighlight(0);
      setIsSpinning(false);
      setShowResult(false);
      setSelectedPower(null);
    }
  }, [isOpen, maxStreakLoss, state.advanced?.roulettePowers, state.currentPlayerIndex, state.mysteryTarget?.playerId, state.categories]);

  const spinRoulette = () => {
    if (isSpinning) return;
    
    setIsSpinning(true);
    playSound('mysteryActivated');
    
    // Déterminer le pouvoir final basé sur preSelectedPower du serveur
  let finalPowerIndex = 0;
    if (preSelectedPower) {
      // Trouver l'index correspondant au pouvoir présélectionné
      finalPowerIndex = rouletteSlots.findIndex(slot => 
        slot.id === preSelectedPower.id
      );
      if (finalPowerIndex === -1) finalPowerIndex = 0;
    }
  // Si rien de présélectionné, tomber sur la case actuelle pour ne pas bloquer
    
    // Animation en phases avec réduction progressive de la vitesse
    let currentIndex = 0;
    let spins = 0;
    const totalSpins = 25 + Math.floor(Math.random() * 10); // 25-35 tours
    const maxSpeed = 50; // millisecondes minimum entre les étapes
    const minSpeed = 200; // millisecondes maximum à la fin
    
    const animateRoulette = () => {
      if (spins >= totalSpins) {
        // Animation terminée - se positionner sur le pouvoir final
        setCurrentHighlight(finalPowerIndex);
        setIsSpinning(false);
        
        setTimeout(() => {
          const finalPower = rouletteSlots[finalPowerIndex];
          setSelectedPower(finalPower);
          setShowResult(true);
          
          setTimeout(() => {
            onPowerSelected(finalPower);
          }, 1200);
        }, 500);
        
        return;
      }
      
      // Calculer la vitesse progressive (ralentit vers la fin)
      const progress = spins / totalSpins;
      const speed = maxSpeed + (minSpeed - maxSpeed) * Math.pow(progress, 2);
      
      currentIndex = (currentIndex + 1) % rouletteSlots.length;
      setCurrentHighlight(currentIndex);
      spins++;
      
      setTimeout(animateRoulette, speed);
    };
    
    // Démarrer l'animation après un court délai
    setTimeout(animateRoulette, 200);
  };

  // Gérer l'ouverture automatique de la roulette
  useEffect(() => {
    if (isOpen && rouletteSlots.length > 0 && !isSpinning && !showResult) {
      const timer = setTimeout(() => {
        spinRoulette();
      }, 800);
      return () => clearTimeout(timer);
    }
    // Fallback: si pas de slots (erreur), fermer proprement sans bloquer
    if (isOpen && rouletteSlots.length === 0) {
      const fallback = setTimeout(() => {
        const defaultPower = { id: 'minus1', value: 1, name: '-1', description: '', image: '' } as any;
        onPowerSelected(defaultPower);
      }, 1200);
      return () => clearTimeout(fallback);
    }
  }, [isOpen, rouletteSlots.length, isSpinning, showResult]);

  const renderRouletteSlot = (slot: Power, index: number) => {
    const isHighlighted = currentHighlight === index;
    const angle = (360 / rouletteSlots.length) * index;
    const radius = 180;
    const centerX = 250;
    const centerY = 250;
    
    const x = centerX + radius * Math.cos((angle - 90) * Math.PI / 180);
    const y = centerY + radius * Math.sin((angle - 90) * Math.PI / 180);
    
    // Déterminer le type de pouvoir pour la couleur
    const isBonus = slot.value < 0;
    const isSwap = slot.value >= 100;
    
    // Définir les couleurs selon le type
    const colors = isSwap ? {
      highlighted: '#FF8C00', // Orange pour swap
      normal: '#FFA500',      // Orange plus clair
      glow: '#FF8C00'
    } : isBonus ? {
      highlighted: '#00FF88', // Vert pour bonus
      normal: '#4FD1C7',      // Vert plus clair
      glow: '#00FF88'
    } : {
      highlighted: '#FFD700', // Jaune pour malus
      normal: '#4A90E2',      // Bleu pour malus
      glow: '#FFD700'
    };
    
    return (
      <Box
        key={`${slot.id}-${index}`}
        position="absolute"
        left={`${x}px`}
        top={`${y}px`}
        transform="translate(-50%, -50%)"
        width="80px"
        height="80px"
        borderRadius="50%"
        border={`3px solid ${isHighlighted ? colors.highlighted : colors.normal}`}
        bg={isHighlighted ? `rgba(${isSwap ? '255, 140, 0' : isBonus ? '0, 255, 136' : '255, 215, 0'}, 0.2)` : `rgba(${isSwap ? '255, 165, 0' : isBonus ? '79, 209, 199' : '74, 144, 226'}, 0.1)`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexDirection="column"
        cursor="pointer"
        transition="all 0.2s ease"
        boxShadow={isHighlighted ? `0 0 20px ${colors.glow}` : 'none'}
        backdropFilter="blur(10px)"
        _hover={{
          transform: 'translate(-50%, -50%) scale(1.05)',
        }}
      >
        <Box
          as="img"
          src={slot.image}
          alt={slot.name}
          width="40px"
          height="40px"
          mb="2px"
          filter={isHighlighted ? 'brightness(1.3)' : 'brightness(1)'}
        />
        <Text
          fontSize="10px"
          fontWeight="bold"
          color={isHighlighted ? colors.highlighted : colors.normal}
          textAlign="center"
          lineHeight="1"
        >
          {slot.name}
        </Text>
      </Box>
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      size="4xl"
    >
      <ModalOverlay bg="rgba(0, 0, 0, 0.8)" backdropFilter="blur(10px)" />
  <ModalContent
        bg="linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
        border="2px solid var(--bleu-electrique)"
        borderRadius="20px"
        boxShadow="0 0 50px rgba(0, 150, 255, 0.3)"
        color="white"
        maxW="700px"
      >
    <ModalHeader textAlign="center" bg="var(--bleu-electrique)" color="white">
          <VStack spacing={2}>
            <Text fontSize="xl" fontWeight="bold">
      {/* Titre neutre pour éviter tout spoil avant le résultat */}
      ROUE MYSTÉRIEUSE
            </Text>
            <Text fontSize="sm" opacity={0.9}>
      {/* Sous-titre neutre, dévoilé après résultat via le bloc résultat */}
      Le destin choisit un effet...
            </Text>
          </VStack>
        </ModalHeader>
        
        <ModalBody p={8}>
          <VStack spacing={8}>
            {/* Cercle de la roulette */}
            <Box position="relative" width="500px" height="500px">
              {/* Cercle de fond */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                width="400px"
                height="400px"
                borderRadius="50%"
                border="4px solid var(--bleu-electrique)"
                bg="rgba(0, 150, 255, 0.05)"
                boxShadow="0 0 30px rgba(0, 150, 255, 0.2)"
              />
              
              {/* Cases de la roulette */}
              {rouletteSlots.map((slot, index) => renderRouletteSlot(slot, index))}

              {/* Centre de la roulette */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                width="60px"
                height="60px"
                borderRadius="50%"
                bg="var(--bleu-electrique)"
                border="3px solid white"
                display="flex"
                alignItems="center"
                justifyContent="center"
                boxShadow="0 0 20px rgba(0, 150, 255, 0.5)"
                zIndex={10}
              >
                <Text fontSize="24px">⚡</Text>
              </Box>
            </Box>

            {/* Messages d'état */}
            <VStack spacing={4}>
              {/* Légende des couleurs */}
              {!isSpinning && !showResult && (
                <VStack spacing={2}>
                  <HStack spacing={6} justify="center">
                    <HStack spacing={2}>
                      <Box width="15px" height="15px" bg="#4A90E2" borderRadius="50%" />
                      <Text fontSize="sm" color="#4A90E2">Malédictions</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Box width="15px" height="15px" bg="#00FF88" borderRadius="50%" />
                      <Text fontSize="sm" color="#00FF88">Bénédictions</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Box width="15px" height="15px" bg="#FF8C00" borderRadius="50%" />
                      <Text fontSize="sm" color="#FF8C00">Vol de Streak</Text>
                    </HStack>
                  </HStack>
                  <Text fontSize="xs" opacity={0.7} textAlign="center">
                    ⚡ Malédictions retirent des points • ✨ Bénédictions ajoutent des points aléatoires • 🔄 Vol transfert les points vers l'attaquant
                  </Text>
                </VStack>
              )}

              {isSpinning && (
                <VStack spacing={2}>
                  <Text fontSize="lg" fontWeight="bold" color="var(--bleu-electrique)">
                    ✨ LES FORCES MYSTIQUES S'ALIGNENT...
                  </Text>
                  <Text fontSize="sm" opacity={0.8}>
                    {isAttackerWinner ? 
                      "Le destin tisse ses fils autour de la cible..." : 
                      "Le maléfice cherche sa cible dans l'éther sombre..."
                    }
                  </Text>
                </VStack>
              )}

              {showResult && selectedPower && (
                <VStack spacing={4}>
                  <Box
                    p={6}
                    bg="linear-gradient(135deg, rgba(0, 150, 255, 0.2) 0%, rgba(0, 255, 150, 0.2) 100%)"
                    borderRadius="15px"
                    border="2px solid var(--bleu-electrique)"
                    textAlign="center"
                    boxShadow="0 0 30px rgba(0, 150, 255, 0.3)"
                  >
                    <HStack spacing={4} justify="center">
                      <Box
                        as="img"
                        src={selectedPower.image}
                        alt={selectedPower.name}
                        width="60px"
                        height="60px"
                      />
                      <VStack align="start" spacing={1}>
                        <Text fontSize="2xl" fontWeight="bold" color="var(--bleu-electrique)">
                          {selectedPower.name}
                        </Text>
                        <Text fontSize="md" opacity={0.9}>
                          {selectedPower.description}
                        </Text>
                        <Text fontSize="sm" opacity={0.9}>
                          {isAttackerWinner
                            ? `${attackerPlayerName} affecte ${targetPlayerName}`
                            : `${attackerPlayerName} subit l'effet`}
                        </Text>
                      </VStack>
                    </HStack>
                  </Box>
                  
                  <Text fontSize="lg" fontWeight="bold" color="lime">
                    🎯 LE DESTIN A PARLÉ !
                  </Text>
                </VStack>
              )}
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default PowerRoulette;
